"use client";

import { alpineResort } from "@/lib/maps/alpine-resorts";
import { useId, useState, type ReactNode } from "react";
import { Mountain, Route, ChevronDown, ChevronUp } from "lucide-react";
import type { PlannerData } from "@/lib/planner/data";
import type { AreaSport } from "@/lib/planner/graph-types";
import { MountainFeedbackLink } from "./MountainFeedbackLink";

export function MountainWorkspace({ data, sport, map, children, hasRoute, planning }: {
  data: PlannerData; sport: AreaSport; map: ReactNode; children: ReactNode; hasRoute: boolean; planning: boolean;
}) {
  const routeStarted = hasRoute || planning;
  const [tools, setTools] = useState({ routeStarted, open: routeStarted });
  if (tools.routeStarted !== routeStarted) setTools({ routeStarted, open: tools.open || routeStarted });
  const controlsId = useId();
  const open = tools.open;
  const areaName = data.area.slug === "espace-killy" ? "Tignes & Val d’Isère" : data.area.name;
  return <div className="min-w-0" data-mountain-workspace>
    <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-xl">
        <h2 className="font-heading text-[26px] font-bold leading-tight text-navy md:text-[32px]">Explore {areaName}</h2>
        <p className="mt-2 text-sm leading-relaxed text-text-muted">Find your next {sport === "ski" ? "run" : "trail"}, a place to stop and a route through your day.</p>
      </div>
      <button type="button" aria-expanded={open} aria-controls={controlsId} onClick={() => {
        setTools(value => ({ ...value, open: !value.open }));
        if (!open) requestAnimationFrame(() => document.getElementById(controlsId)?.scrollIntoView({ block: "nearest" }));
      }} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-navy px-4 text-sm font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal">
        <Route size={18} aria-hidden />{hasRoute ? "Your route" : "Plan my route"}{open ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
      </button>
    </div>
    <div className={`grid min-w-0 items-start gap-5 ${open ? "xl:grid-cols-[minmax(0,1fr)_360px]" : ""}`}>
      <div className="min-w-0">
        <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted"><span>Forecast: {data.scoredDate}</span><span>{data.trailScoresAvailable ? "Use See the day to explore scores and sunlight" : "Run forecasts unavailable"}</span>{data.area.availability !== "open" && <span>Season preview · check operator openings</span>}</div>
        {map}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4">
          <MountainFeedbackLink resort={data.entrySlug} sport={sport} source={hasRoute ? "route" : "map"} />
          <p className="text-xs text-text-muted">No account needed. Tell us what works or looks wrong.</p>
        </div>
        <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-text-muted"><Mountain size={15} className="mt-0.5 shrink-0" aria-hidden />{alpineResort(data.area.slug, sport) ? "Mapped terrain with illustrated snow, woodland and moving lifts. Layers lets you choose dramatic relief or true scale. Scenery and carrier movement are illustrative, not live observations." : "Explore mapped terrain, trails, lifts and places. Use Map sources for the official resort map and source details."}</p>
      </div>
      <div id={controlsId} hidden={!open} role="region" aria-label="Route planner" tabIndex={0}
        className="min-w-0 scroll-mt-56 rounded-2xl border border-border-light bg-white p-4 focus-visible:outline-2 focus-visible:outline-teal md:p-5 xl:max-h-[680px] xl:overflow-y-auto xl:overscroll-y-contain">
        <h3 className="mb-4 font-heading text-xl font-bold text-navy">{hasRoute ? "Your day on the mountain" : "Route planner"}</h3>
        {children}
      </div>
    </div>
  </div>;
}
